/**
 * ScrollSpy - Native JavaScript implementation
 * Tracks scroll position and highlights active navigation items
 * Supports multiple instances on a single page
 * Auto-calculates offset based on header height + WP Admin Bar
 */

const usedHeadingIds = new Set();
const contentHeadingMap = new WeakMap();
let menuItemCounter = 7056;

const slugifyHeading = (value) => {
    const rawValue = value ? value.toString() : '';
    const normalized = typeof rawValue.normalize === 'function' ? rawValue.normalize('NFKD') : rawValue;
    return normalized
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const getUniqueSlug = (baseSlug, element) => {
    let slug = baseSlug;
    let suffix = 1;

    while (
        !slug ||
        usedHeadingIds.has(slug) ||
        (document.getElementById(slug) && document.getElementById(slug) !== element)
    ) {
        slug = `${baseSlug}-${suffix}`;
        suffix += 1;
    }

    usedHeadingIds.add(slug);
    return slug;
};

const getHeadingData = (content) => {
    if (contentHeadingMap.has(content)) {
        return contentHeadingMap.get(content);
    }

    const items = [];
    const headings = Array.from(content.querySelectorAll('h2'));

    headings.forEach((heading) => {
        const rawText = heading.textContent || heading.innerText || '';
        const label = rawText.trim();
        if (!label) {
            return;
        }

        const baseSlug = slugifyHeading(label);
        if (!baseSlug) {
            return;
        }

        const slug = getUniqueSlug(baseSlug, heading);
        heading.id = slug;

        items.push({
            id: slug,
            label,
        });
    });

    contentHeadingMap.set(content, items);
    return items;
};

const findContentForNavigation = (nav) => {
    const scopeCandidates = [
        nav.closest('.row-area-content'),
        nav.closest('section'),
    ].filter(Boolean);

    for (const scope of scopeCandidates) {
        const content = scope.querySelector('.scrollspy-content');
        if (content) {
            return content;
        }
    }

    return document.querySelector('.scrollspy-content');
};

const createScrollSpyMenu = (items) => {
    const ul = document.createElement('ul');
    ul.id = 'menu-scroll-spy';
    ul.className = 'sidebar-menu scroll-spy';

    items.forEach((item, index) => {
        const li = document.createElement('li');
        const menuItemId = `menu-item-${menuItemCounter}`;
        menuItemCounter += 1;

        li.className = `menu-item`;

        if (index === 0) {
            li.classList.add('active');
        }

        const anchor = document.createElement('a');
        anchor.setAttribute('href', `#${item.id}`);
        anchor.textContent = item.label;

        if (index === 0) {
            anchor.classList.add('active');
        }

        li.appendChild(anchor);
        ul.appendChild(li);
    });

    return ul;
};

const rebuildScrollSpyNavigation = () => {
    document.querySelectorAll('.scrollspy-content').forEach((content) => {
        getHeadingData(content);
    });

    document.querySelectorAll('.scrollspy-navigation').forEach((nav) => {
        const content = findContentForNavigation(nav);
        if (!content) {
            return;
        }

        const items = getHeadingData(content);
        if (!items.length) {
            return;
        }

        const menu = createScrollSpyMenu(items);
        nav.innerHTML = '';
        nav.appendChild(menu);
    });
};

class ScrollSpy {
    constructor(element, options = {}) {
      this.nav = element;
      this.links = Array.from(this.nav.querySelectorAll('a[href^="#"]'));
      this.sections = [];
      this.activeClass = options.activeClass || 'active';
      this.customOffset = options.offset || null;
      this.offsetPadding = options.offsetPadding || 20;
      this.throttleDelay = options.throttleDelay || 100;
      this.headerSelector = options.headerSelector || 'header.site-header';
      this.wpAdminBarSelector = options.wpAdminBarSelector || '#wpadminbar';

      this.init();
    }

    /**
     * Calculate offset dynamically based on header height + WP Admin Bar
     */
    getOffset() {
      if (this.customOffset !== null) {
        return this.customOffset;
      }

      let totalOffset = 0;

      // Get header height
      const header = document.querySelector(this.headerSelector);
      if (header) {
        totalOffset += header.offsetHeight;
      }

      // Get WP Admin Bar height (if exists)
      const wpAdminBar = document.querySelector(this.wpAdminBarSelector);
      if (wpAdminBar) {
        totalOffset += wpAdminBar.offsetHeight;
      }

      // Add padding
      totalOffset += this.offsetPadding;

      return totalOffset;
    }

    init() {
      // Get all target sections
      this.sections = this.links
        .map(link => {
          const href = link.getAttribute('href');
          const target = document.querySelector(href);
          if (target) {
            return {
              link: link,
              section: target,
              id: href
            };
          }
          return null;
        })
        .filter(item => item !== null);

      if (this.sections.length === 0) {
        console.warn('ScrollSpy: No valid sections found');
        return;
      }

      // Bind events
      this.scrollHandler = this.throttle(() => this.onScroll(), this.throttleDelay);
      window.addEventListener('scroll', this.scrollHandler, { passive: true });

      // Recalculate offset on resize (in case header height changes)
      this.resizeHandler = this.throttle(() => this.onScroll(), 200);
      window.addEventListener('resize', this.resizeHandler, { passive: true });

      // Smooth scroll on click
      this.links.forEach(link => {
        link.addEventListener('click', (e) => this.handleClick(e));
      });

      // Initial check
      this.onScroll();
    }

    onScroll() {
      const triggerPoint = this.getOffset() - (window.innerHeight * 0.8);
      const scrollPosition = window.scrollY + triggerPoint;
      let currentSection = null;

      // Find the current section
      for (let i = this.sections.length - 1; i >= 0; i--) {
        const section = this.sections[i];
        const sectionTop = section.section.offsetTop;

        if (scrollPosition >= sectionTop) {
          currentSection = section;
          break;
        }
      }

      // Update active states
      this.sections.forEach(section => {
        const parentLi = section.link.closest('li');
        if (section === currentSection) {
          section.link.classList.add(this.activeClass);
          if (parentLi) parentLi.classList.add(this.activeClass);
        } else {
          section.link.classList.remove(this.activeClass);
          if (parentLi) parentLi.classList.remove(this.activeClass);
        }
      });
    }

    handleClick(e) {
      e.preventDefault();
      const href = e.currentTarget.getAttribute('href');
      const target = document.querySelector(href);

      if (target) {
        const offset = this.getOffset() - 32;

        // Calculate target position relative to document
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset;

        // Subtract offset to stop ABOVE the target
        const scrollToPosition = targetTop - offset;

        window.scrollTo({
          top: scrollToPosition,
          behavior: 'smooth'
        });

        // Update URL without triggering scroll
        if (history.pushState) {
          history.pushState(null, null, href);
        }
      }
    }

    throttle(func, delay) {
      let timeoutId;
      let lastRan;

      return function() {
        const context = this;
        const args = arguments;

        if (!lastRan) {
          func.apply(context, args);
          lastRan = Date.now();
        } else {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(function() {
            if ((Date.now() - lastRan) >= delay) {
              func.apply(context, args);
              lastRan = Date.now();
            }
          }, delay - (Date.now() - lastRan));
        }
      };
    }

    destroy() {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.resizeHandler);

      this.sections.forEach(section => {
        section.link.classList.remove(this.activeClass);
        const parentLi = section.link.closest('li');
        if (parentLi) parentLi.classList.remove(this.activeClass);
      });
    }
  }

  // Auto-initialize all scroll-spy elements
  function initScrollSpies() {
    rebuildScrollSpyNavigation();
    const scrollSpyElements = Array.from(document.querySelectorAll('.scroll-spy')).filter(Boolean);
    const instances = [];

    scrollSpyElements.forEach(element => {
      if (!(element instanceof HTMLElement)) {
        return;
      }
      const instance = new ScrollSpy(element, {
        activeClass: 'active',
        offsetPadding: 60, // Additional padding
        throttleDelay: 100,
        headerSelector: 'header.site-header',
        wpAdminBarSelector: '#wpadminbar' // WordPress admin bar
      });
      instances.push(instance);
    });

    return instances;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScrollSpies);
  } else {
    initScrollSpies();
  }

  // Export for module usage
  export { ScrollSpy, initScrollSpies };
